import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Button,
  Paper,
  Stack,
  Tooltip,
  Avatar,
  IconButton,
  Alert,
  alpha,
  useTheme,
  Snackbar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  HelpOutline as HelpIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Lock as PrivateIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

import { BaseDialog } from '../ui/BaseDialog';
import { ApprovalStatusBadge } from './ApprovalStatusBadge';
import { FAQDocumentResponse, DocumentVisibility } from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { getUserDisplayName, formatDateTime } from '../../utils/formatting/formatters';
import { useFAQ } from '../../hooks/api/useFAQs';

interface FAQDetailDialogProps {
  open: boolean;
  onClose: () => void;
  faqId: string | null;
  onEdit?: (faq: FAQDocumentResponse) => void;
  onDelete?: (faq: FAQDocumentResponse) => void;
  isModerator: boolean;
  currentUserId: string | null;
  onRateFAQ?: (faqId: string, helpful: boolean) => void;
  onUnrateFAQ?: (faqId: string) => void;
}

export const FAQDetailDialog: React.FC<FAQDetailDialogProps> = ({
  open,
  onClose,
  faqId,
  onEdit,
  onDelete,
  isModerator,
  currentUserId,
  onRateFAQ,
  onUnrateFAQ,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [copiedId, setCopiedId] = useState(false);

  // Fetch FAQ data using the hook - this will automatically update when the cache changes
  const { data: faq, isLoading } = useFAQ(faqId || '', { enabled: Boolean(faqId) });

  // Compute canEdit based on moderator status or ownership
  const canEdit = isModerator || (currentUserId && faq?.owner_id === currentUserId);

  React.useEffect(() => {
    if (open && faq) {
      log.user.action('faq_detail_viewed', 'FAQDetailDialog', {
        faqId: faq.document_id,
        question: faq.question?.substring(0, 50) ?? null,
      });
    }
  }, [open, faq]);

  // Reset copied state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setCopiedId(false);
    }
  }, [open]);

  const handleCopyId = useCallback(() => {
    if (faq?.document_id) {
      navigator.clipboard.writeText(faq.document_id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
      log.user.action('faq_id_copied', 'FAQDetailDialog', { faqId: faq.document_id });
    }
  }, [faq?.document_id]);

  const handleDelete = useCallback(() => {
    if (faq && onDelete) {
      onDelete(faq);
      onClose(); // Close the dialog after triggering delete
    }
  }, [faq, onDelete, onClose]);

  const handleThumbsUp = useCallback(() => {
    if (!faq || !onRateFAQ || !onUnrateFAQ) return;

    if (faq.user_vote === 'helpful') {
      onUnrateFAQ(faq.document_id);
    } else {
      onRateFAQ(faq.document_id, true);
    }
  }, [faq, onRateFAQ, onUnrateFAQ]);

  const handleThumbsDown = useCallback(() => {
    if (!faq || !onRateFAQ || !onUnrateFAQ) return;

    if (faq.user_vote === 'not_helpful') {
      onUnrateFAQ(faq.document_id);
    } else {
      onRateFAQ(faq.document_id, false);
    }
  }, [faq, onRateFAQ, onUnrateFAQ]);

  // Don't render if dialog is closed or FAQ is loading/not available
  if (!open || isLoading || !faq) {
    return null;
  }

  const getVisibilityIcon = (visibility: DocumentVisibility) => {
    switch (visibility) {
      case DocumentVisibility.Public:
        return <PublicIcon fontSize="small" color="success" />;
      case DocumentVisibility.Organization:
        return <BusinessIcon fontSize="small" color="primary" />;
      case DocumentVisibility.Group:
        return <GroupIcon fontSize="small" color="secondary" />;
      case DocumentVisibility.Private:
        return <PrivateIcon fontSize="small" color="disabled" />;
      default:
        return <HelpIcon fontSize="small" />;
    }
  };

  const getVisibilityText = () => {
    if (!faq.visibility) {
      return t('faq.permissions.organization');
    }

    switch (faq.visibility) {
      case DocumentVisibility.Public:
        return t('faq.permissions.public');
      case DocumentVisibility.Organization:
        return t('faq.permissions.organization');
      case DocumentVisibility.Group:
        const groups = faq.shared_with_groups || [];
        return t('faq.permissions.group', { groups: groups.join(', ') });
      case DocumentVisibility.Private:
        return t('faq.permissions.private');
      default:
        return t('faq.permissions.organization');
    }
  };

  const handleEdit = () => {
    if (onEdit && faq) {
      log.user.action('faq_edit_from_detail', 'FAQDetailDialog', { faqId: faq.document_id });
      onEdit(faq);
      onClose();
    }
  };

  // Calculate total votes and helpful percentage
  const totalVotes = (faq.helpful_count || 0) + (faq.not_helpful_count || 0);
  const helpfulPercentage = totalVotes > 0
    ? Math.round(((faq.helpful_count || 0) / totalVotes) * 100)
    : 0;

  // Markdown components with enhanced styling
  const markdownComponents = {
    p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <Typography
        variant="body1"
        component="p"
        sx={{
          mb: 2,
          lineHeight: 1.8,
          fontSize: '1rem',
        }}
        {...props}
      />
    ),
    h1: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mt: 3,
          mb: 2,
          fontWeight: 700,
          color: 'text.primary',
        }}
        {...props}
      />
    ),
    h2: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography
        variant="h5"
        component="h2"
        sx={{
          mt: 2.5,
          mb: 1.5,
          fontWeight: 600,
          color: 'text.primary',
        }}
        {...props}
      />
    ),
    h3: ({ ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <Typography
        variant="h6"
        component="h3"
        sx={{
          mt: 2,
          mb: 1,
          fontWeight: 600,
          color: 'text.primary',
        }}
        {...props}
      />
    ),
    ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <Box component="ul" sx={{ pl: 3, my: 1.5, '& li': { mb: 0.75 } }} {...props} />
    ),
    ol: ({ ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
      <Box component="ol" sx={{ pl: 3, my: 1.5, '& li': { mb: 0.75 } }} {...props} />
    ),
    li: ({ ...props }: React.ComponentProps<'li'>) => (
      <Typography
        component="li"
        variant="body1"
        sx={{
          lineHeight: 1.8,
          fontSize: '1rem',
        }}
        {...props}
      />
    ),
    a: ({ href, children, ...props }: React.ComponentProps<'a'>) => (
      <Box
        component="a"
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          textDecorationColor: alpha(theme.palette.primary.main, 0.4),
          transition: 'all 0.2s',
          '&:hover': {
            textDecorationColor: theme.palette.primary.main,
            color: 'primary.dark',
          },
        }}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </Box>
    ),
    code: ({ ...props }: React.ComponentProps<'code'>) => (
      <Box
        component="code"
        sx={{
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: 'primary.dark',
          padding: '3px 8px',
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.875em',
          fontWeight: 500,
        }}
        {...props}
      />
    ),
    blockquote: ({ ...props }: React.ComponentProps<'blockquote'>) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          pl: 2,
          py: 0.5,
          my: 2,
          ml: 0,
          fontStyle: 'italic',
          color: 'text.secondary',
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        }}
        {...props}
      />
    ),
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={faq.question}
      maxWidth="md"
      fullWidth
      actions={
        <>
          {isModerator && onDelete && (
            <Button
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              variant="outlined"
              color="error"
              aria-label={t('faq.actions.delete')}
            >
              {t('faq.actions.delete')}
            </Button>
          )}
          {canEdit && onEdit && (
            <Button
              startIcon={<EditIcon />}
              onClick={handleEdit}
              variant="outlined"
              aria-label={t('faq.actions.edit')}
            >
              {t('faq.actions.edit')}
            </Button>
          )}
          <Button onClick={onClose} variant="contained">
            {t('common.close', 'Close')}
          </Button>
        </>
      }
    >
      <Box sx={{ pb: 1 }}>
        {/* Header with badges and metadata */}
        <Box sx={{ mb: 3 }}>
          <Stack spacing={2}>
            {/* Badges Row */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <ApprovalStatusBadge status={faq.approval_status || 'draft'} />

              <Tooltip title={getVisibilityText()} arrow>
                <Chip
                  icon={getVisibilityIcon(faq.visibility || DocumentVisibility.Organization)}
                  label={getVisibilityText()}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>

              {faq.language && (
                <Chip
                  label={faq.language === 'de' ? 'Deutsch' : 'English'}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Categories */}
            {faq.category_tags && faq.category_tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {faq.category_tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    color="secondary"
                    variant="filled"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Box>
            )}

            {/* Quick Stats Bar */}
            {totalVotes > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.success.main, 0.04),
                  borderColor: alpha(theme.palette.success.main, 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon fontSize="small" color="success" />
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    {helpfulPercentage}% {t('faq.detail.helpful', 'Helpful')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>

        {/* Answer Section - Main Content */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              '& > *:first-of-type': { mt: 0 },
              '& > *:last-child': { mb: 0 },
              color: 'text.primary',
            }}
          >
            <ReactMarkdown components={markdownComponents}>
              {faq.answer}
            </ReactMarkdown>
          </Box>
        </Paper>

        {/* Vote Section - Interactive */}
        {onRateFAQ && onUnrateFAQ && (
          <>
            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                {t('faq.detail.wasThisHelpful', 'Was this helpful?')}
              </Typography>
            </Divider>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Tooltip
                title={faq.user_vote === 'helpful' ? t('faq.detail.removeVote', 'Remove vote') : t('faq.actions.helpful', 'Helpful')}
                arrow
              >
                <Button
                  variant={faq.user_vote === 'helpful' ? 'contained' : 'outlined'}
                  color="success"
                  size="large"
                  startIcon={<ThumbUpIcon />}
                  onClick={handleThumbsUp}
                  aria-pressed={faq.user_vote === 'helpful'}
                  aria-label={t('faq.actions.helpful', 'Helpful')}
                  sx={{
                    minWidth: 140,
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    ...(faq.user_vote === 'helpful' && {
                      boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.15)}`,
                    }),
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {faq.helpful_count || 0}
                </Button>
              </Tooltip>

              <Tooltip
                title={faq.user_vote === 'not_helpful' ? t('faq.detail.removeVote', 'Remove vote') : t('faq.actions.not_helpful', 'Not helpful')}
                arrow
              >
                <Button
                  variant={faq.user_vote === 'not_helpful' ? 'contained' : 'outlined'}
                  color="error"
                  size="large"
                  startIcon={<ThumbDownIcon />}
                  onClick={handleThumbsDown}
                  aria-pressed={faq.user_vote === 'not_helpful'}
                  aria-label={t('faq.actions.not_helpful', 'Not helpful')}
                  sx={{
                    minWidth: 140,
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    ...(faq.user_vote === 'not_helpful' && {
                      boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0.15)}`,
                    }),
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {faq.not_helpful_count || 0}
                </Button>
              </Tooltip>
            </Box>
          </>
        )}

        {/* Metadata Section - Condensed */}
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container spacing={2}>
            {/* Creator Info */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('faq.detail.createdBy', 'Created by')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {getUserDisplayName(faq, t('common.unknown_user', 'Unknown User'))}
                  </Typography>
                  {faq.owner_email && faq.owner_email !== faq.owner_full_name && faq.owner_email !== faq.owner_username && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {faq.owner_email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Dates */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack spacing={1}>
                {faq.created_at && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('faq.detail.created', 'Created')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDateTime(faq.created_at, i18n.language)}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {faq.updated_at && faq.updated_at !== faq.created_at && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('faq.detail.lastUpdated', 'Last updated')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDateTime(faq.updated_at, i18n.language)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Grid>

            {/* Document ID */}
            {faq.document_id && (
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('faq.detail.id', 'Document ID')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        wordBreak: 'break-all',
                      }}
                    >
                      {faq.document_id}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={copiedId ? t('faq.detail.copied', 'Copied!') : t('faq.detail.copyId', 'Copy ID')}
                    arrow
                  >
                    <IconButton
                      size="small"
                      onClick={handleCopyId}
                      aria-label={t('faq.detail.copyId', 'Copy ID')}
                      sx={{
                        color: copiedId ? 'success.main' : 'text.secondary',
                        transition: 'all 0.2s',
                      }}
                    >
                      {copiedId ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </BaseDialog>
  );
};
